import { UserPreferencesResponse } from '@shared/core/preferences'
import { GetProfileResponseDto } from '@shared/core/users'
import { formatInTimeZone } from 'date-fns-tz'
import { Info, TriangleAlert } from 'lucide-react'
import Tooltip from 'rc-tooltip'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getEventDate } from 'src/helpers/get-event-date'
import { formatTimezoneDisplayLabel } from 'src/helpers/timezones'
import { FormattedText, Typography } from 'src/modules/core/components'
import { useDisplayTimezone } from 'src/modules/core/context'
import type { EventData } from 'src/modules/event/types/event.types'
import { formatEventDate } from 'src/modules/event/utils/helpers'
import {
  formatEventDateTimeForMessage,
  getEffectiveMessageTimezone,
  shouldWarnTimezoneDateTimePlaceholderMismatch,
} from 'src/modules/event/utils/message-builder'
import type { CalendarDto } from 'src/types/calendar.dto'
import {
  buildEditEventTimezoneMismatchDismissStorageKey,
  isEditEventTimezoneMismatchDismissed,
  setEditEventTimezoneMismatchDismissed,
} from './edit-event-timezone-mismatch-dismissal'
import classes from './edit-event-timezone-placeholder-mismatch-alert.module.scss'

export type EditEventTimezonePlaceholderMismatchAlertProps = {
  event: EventData
  profileData: GetProfileResponseDto
  preferences: UserPreferencesResponse
  calendar: CalendarDto | undefined
  confirmation_message: string
  reminder_message: string
  follow_up_message: string
  confirmation_enabled: boolean
  reminder_enabled: boolean
  follow_up_enabled: boolean
}

export type TimezonePlaceholderMismatchPreviewParts = {
  displayDate: string
  displayTime: string
  smsDate: string
  smsTime: string
  allDay: boolean
  smsTimeInMessage: string
}

export function useEditEventTimezonePlaceholderMismatchData({
  event,
  profileData,
  preferences,
  calendar,
  confirmation_message,
  reminder_message,
  follow_up_message,
  confirmation_enabled,
  reminder_enabled,
  follow_up_enabled,
}: EditEventTimezonePlaceholderMismatchAlertProps) {
  const { timezone } = useDisplayTimezone()

  const eventDate = useMemo(() => getEventDate(event), [event])
  const originalEventDate = useMemo(
    () => new Date(Number(event.original_event_date ?? event.event_date)),
    [event.original_event_date, event.event_date],
  )

  const eventForMessages = useMemo(
    () => ({
      ...event,
      confirmation_message,
      reminder_message,
      follow_up_message,
    }),
    [event, confirmation_message, reminder_message, follow_up_message],
  )

  const shouldWarn = useMemo(() => {
    if (!calendar || !profileData) {
      return false
    }

    return shouldWarnTimezoneDateTimePlaceholderMismatch({
      profile: profileData,
      preferences,
      event: eventForMessages,
      calendar,
      displayTimezone: timezone,
      confirmationEnabled: Boolean(confirmation_enabled),
      reminderEnabled: Boolean(reminder_enabled),
      followUpEnabled: Boolean(follow_up_enabled),
    })
  }, [
    calendar,
    profileData,
    preferences,
    eventForMessages,
    timezone,
    confirmation_enabled,
    reminder_enabled,
    follow_up_enabled,
  ])

  const dismissStorageKey = useMemo(() => {
    if (!calendar || !profileData) {
      return null
    }
    if (!calendar.id) {
      return null
    }
    const effectiveTz = getEffectiveMessageTimezone(eventForMessages, calendar, profileData) ?? timezone
    return buildEditEventTimezoneMismatchDismissStorageKey(calendar.id, timezone, effectiveTz)
  }, [calendar, profileData, eventForMessages, timezone])

  const previewParts = useMemo((): TimezonePlaceholderMismatchPreviewParts | null => {
    if (!calendar || !profileData) {
      return null
    }

    const smsTimezoneResolved = getEffectiveMessageTimezone(eventForMessages, calendar, profileData) ?? timezone
    const dateOrder = profileData.google_calendar_date_order
    const { time: smsTimeInMessage } = formatEventDateTimeForMessage(eventForMessages, preferences, smsTimezoneResolved)

    return {
      displayDate: formatEventDate(eventDate, dateOrder, timezone),
      displayTime: formatInTimeZone(originalEventDate, timezone, 'h:mma'),
      smsDate: formatEventDate(eventDate, dateOrder, smsTimezoneResolved),
      smsTime: formatInTimeZone(originalEventDate, smsTimezoneResolved, 'h:mma'),
      allDay: Boolean(event.all_day),
      smsTimeInMessage,
    }
  }, [calendar, profileData, eventForMessages, eventDate, originalEventDate, timezone, event.all_day, preferences])

  const timezoneLabels = useMemo(() => {
    if (!calendar || !profileData) {
      return null
    }
    const effectiveTz = getEffectiveMessageTimezone(eventForMessages, calendar, profileData) ?? timezone
    return {
      displayLabel: formatTimezoneDisplayLabel(timezone, originalEventDate),
      effectiveLabel: formatTimezoneDisplayLabel(effectiveTz, originalEventDate),
    }
  }, [calendar, profileData, eventForMessages, timezone, originalEventDate])

  return {
    shouldWarn,
    dismissStorageKey,
    previewParts,
    eventForMessages,
    timezoneLabels,
  }
}

function buildSentAsPillTooltipText(
  previewParts: TimezonePlaceholderMismatchPreviewParts,
  displayTimeLabel: string,
  smsTimeLabel: string,
): string {
  const showDates = previewParts.displayDate !== previewParts.smsDate
  let seeing: string
  let sms: string

  if (previewParts.allDay) {
    seeing = `${previewParts.displayDate}, all day`
    sms = `${previewParts.smsDate}, all day`
  } else {
    seeing = showDates ? `${previewParts.displayDate}, ${displayTimeLabel}` : displayTimeLabel
    sms = showDates ? `${previewParts.smsDate}, ${smsTimeLabel}` : smsTimeLabel
  }

  return `You're seeing ${seeing} here because of your display timezone, but the SMS will display ${sms}. Just highlighting it so there are no surprises.`
}

export function EditEventTimezoneMismatchSentAsPill(props: EditEventTimezonePlaceholderMismatchAlertProps) {
  const { shouldWarn, previewParts } = useEditEventTimezonePlaceholderMismatchData(props)
  const showPill = Boolean(shouldWarn && previewParts)

  const tooltipText = useMemo(() => {
    if (!previewParts) {
      return ''
    }
    const displayTimeLabel = previewParts.allDay ? 'all day' : previewParts.displayTime
    const smsTimeLabel = previewParts.allDay ? 'all day' : previewParts.smsTimeInMessage
    return buildSentAsPillTooltipText(previewParts, displayTimeLabel, smsTimeLabel)
  }, [previewParts])

  const pillLabel = useMemo(() => {
    if (!previewParts) {
      return ''
    }
    const dateDiff = previewParts.displayDate !== previewParts.smsDate
    const dateSegment = dateDiff ? `${previewParts.smsDate}, ` : ''
    return `Sent as ${dateSegment}${previewParts.smsTimeInMessage}`
  }, [previewParts])

  if (!showPill || !previewParts) {
    return null
  }

  return (
    <span className={classes.sentAsPill}>
      <Typography variant="body" className={classes.sentAsPillText}>
        {pillLabel}
      </Typography>
      <Tooltip
        placement="top"
        overlay={
          <Typography variant="body" className={classes.pillTooltipText}>
            {tooltipText}
          </Typography>
        }
        overlayClassName={classes.pillTooltipOverlay}
      >
        <Info className={classes.pillInfoIcon} aria-label="Timezone mismatch details" tabIndex={0} />
      </Tooltip>
    </span>
  )
}

export function EditEventTimezonePlaceholderMismatchAlert({
  event,
  profileData,
  preferences,
  calendar,
  confirmation_message,
  reminder_message,
  follow_up_message,
  confirmation_enabled,
  reminder_enabled,
  follow_up_enabled,
}: EditEventTimezonePlaceholderMismatchAlertProps) {
  const {
    shouldWarn: shouldShowAlert,
    dismissStorageKey,
    previewParts,
    timezoneLabels,
  } = useEditEventTimezonePlaceholderMismatchData({
    event,
    profileData,
    preferences,
    calendar,
    confirmation_message,
    reminder_message,
    follow_up_message,
    confirmation_enabled,
    reminder_enabled,
    follow_up_enabled,
  })

  const [sessionDismissed, setSessionDismissed] = useState(false)

  useEffect(() => {
    setSessionDismissed(false)
  }, [dismissStorageKey])

  const storedDismissed = useMemo(() => {
    if (!dismissStorageKey) {
      return false
    }
    return isEditEventTimezoneMismatchDismissed(dismissStorageKey)
  }, [dismissStorageKey])

  const dismissed = storedDismissed || sessionDismissed

  useEffect(() => {
    if (!dismissStorageKey) {
      return
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === dismissStorageKey && e.newValue === '1') {
        setSessionDismissed(true)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [dismissStorageKey])

  const handleDismissWarning = useCallback(() => {
    if (!dismissStorageKey) {
      return
    }
    setEditEventTimezoneMismatchDismissed(dismissStorageKey)
    setSessionDismissed(true)
  }, [dismissStorageKey])

  if (!shouldShowAlert || !previewParts || !timezoneLabels || dismissed) {
    return null
  }

  const showDates = previewParts.displayDate !== previewParts.smsDate

  const displayWhenFragment = previewParts.allDay ? (
    showDates ? (
      <>
        <strong>{previewParts.displayDate}</strong>, <FormattedText id="reminder.allDay" />
      </>
    ) : (
      <strong>
        <FormattedText id="reminder.allDay" />
      </strong>
    )
  ) : (
    <>
      {showDates && (
        <>
          <strong>{previewParts.displayDate}</strong>,{' '}
        </>
      )}
      <strong>{previewParts.displayTime}</strong>
    </>
  )

  const reminderWhenFragment = previewParts.allDay ? (
    showDates ? (
      <>
        <strong>{previewParts.smsDate}</strong>, <FormattedText id="reminder.allDay" />
      </>
    ) : (
      <strong>
        <FormattedText id="reminder.allDay" />
      </strong>
    )
  ) : (
    <>
      {showDates && (
        <>
          <strong>{previewParts.smsDate}</strong>,{' '}
        </>
      )}
      <strong>{previewParts.smsTime}</strong>
    </>
  )

  return (
    <div className={classes.alert} role="alert">
      <TriangleAlert className={classes.icon} aria-hidden />
      <div>
        <span className={classes.lead}>The reminder message will show a different time</span>
        <span className={classes.detail}>
          This appointment{' '}
          {previewParts.allDay ? (
            <>shows {displayWhenFragment} because your display timezone is </>
          ) : (
            <>shows a start time of {displayWhenFragment} because your display timezone is </>
          )}
          <strong>{timezoneLabels.displayLabel}</strong>
          {previewParts.allDay ? (
            <>, but the reminder will show {reminderWhenFragment} because this appointment is on a calendar set to </>
          ) : (
            <>
              , but the reminder will show a start time of {reminderWhenFragment} because this appointment is on a
              calendar set to{' '}
            </>
          )}
          <strong>{timezoneLabels.effectiveLabel}</strong>.{' '}
          <Link to="/dashboard/settings?tab=calendars" className={classes.link}>
            Review your calendar timezones here
          </Link>
          {dismissStorageKey ? (
            <>
              {' '}
              or{' '}
              <button
                type="button"
                className={classes.dismissButton}
                onClick={handleDismissWarning}
                aria-label="Dismiss timezone mismatch warning for this calendar at the current display and message timezones"
              >
                dismiss this warning
              </button>{' '}
              for appointments in this calendar.
            </>
          ) : (
            '.'
          )}
        </span>
      </div>
    </div>
  )
}
